import React from 'react';

const LAST_UPDATED_DATE = '24/12/2025';
const CONTACT_EMAIL = 'avatar@columbus.tech';
const CONTACT_DISPLAY = 'Alia HR Avatar <avatar@columbus.tech>';

const Privacy: React.FC = () => {
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: '#FBFBFB',
      }}
    >
      {/* Header */}
      <header
        className="p-6 md:py-4 md:px-[120px]"
        style={{
          borderBottom: '0.5px solid var(--color-beige, #EEEDEC)',
          background: '#FFF',
        }}
      >
        <img src="/images/mic-logo.png" alt="MIC Logo" width={104} height={50} />
      </header>

      {/* Main Content */}
      <main
        className="flex-1 m-6 md:my-8 md:mx-auto p-6 md:p-12"
        style={{
          border: '1px solid #F3F4F6',
          borderRadius: '8px',
          background: '#FFF',
          maxWidth: '800px',
          width: '100%',
        }}
      >
        <h1
          style={{
            color: '#171C27',
            fontFamily: 'Interstate',
            fontSize: '24px',
            fontWeight: 700,
            letterSpacing: '0.6px',
            lineHeight: '100%',
            marginBottom: '24px',
          }}
        >
          CANDIDATE / APPLICANT PRIVACY NOTICE
        </h1>

        <p style={paragraphStyle}>
          This privacy notice applies to Mubadala Investment Company ("<strong>Mubadala</strong>", "<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>") and any of our affiliate entities processing your personal data in connection with your application for employment or engagement with Mubadala.
        </p>

        <p style={paragraphStyle}>
          This privacy notice was last updated on: <strong>{LAST_UPDATED_DATE}</strong>
        </p>

        <h2 style={headingStyle}>About this privacy notice</h2>
        <p style={paragraphStyle}>
          For the purposes of applicable data protection law, we are a data controller in respect of your personal data. We are responsible for ensuring that we use your personal data in compliance with applicable data protection law.
        </p>
        <p style={paragraphStyle}>
          This privacy notice applies if you are applying for work (whether as an employee, worker, contractor, intern, or otherwise) at Mubadala. This privacy notice sets out the basis on which any personal data about you will be processed by us for the purposes of the recruitment process. Please take the time to read and understand this privacy notice.
        </p>
        <p style={paragraphStyle}>
          If your application with us is successful, we shall process your personal data in accordance with our Employee Privacy Notice, which shall apply thereafter.
        </p>

        <h2 style={headingStyle}>Personal data that we collect about you</h2>
        <p style={paragraphStyle}>We will collect and process the following personal data about you:</p>

        <p style={subHeadingStyle}>Information that you provide to us or one of our affiliates</p>
        <p style={paragraphStyle}>
          This includes information that you provide to us when you make your application and participate in the recruitment process. This information may include:
        </p>
        <ul style={listStyle}>
          <li style={listItemStyle}>your full name, date of birth, nationality, marital/dependent status, address, contact details (including telephone number and email address), and identification details or other national identifier;</li>
          <li style={listItemStyle}>any other details you provide in support of your application, including your work and education history, employment references, compensation history, and similar information contained in your CV/resume and/or covering letter and your reasons for applying to Mubadala;</li>
          <li style={listItemStyle}>information provided in connection with visa applications (such as passport and national/state identification numbers) and background screening processes; and</li>
          <li style={listItemStyle}>audio, video, or written responses you provide as part of AI-powered recruitment activities, including biometric data (such as video recordings or voice recordings) for the creation of interview reports.</li>
        </ul>

        <p style={subHeadingStyle}>Information we collect or generate about you</p>
        <p style={paragraphStyle}>
          This includes information that is collected in connection with your application and is likely to include:
        </p>
        <ul style={listStyle}>
          <li style={listItemStyle}>personal data that we collect through your correspondence with us;</li>
          <li style={listItemStyle}>information obtained through any interviews and assessments, including those conducted using AI-powered digital interview tools (such as recorded audio and video responses, reports or analytics produced by AI systems for evaluation purposes); and</li>
          <li style={listItemStyle}>personal data collected when visiting our premises, including CCTV footage and access and location data.</li>
        </ul>

        <p style={subHeadingStyle}>Information we obtain from other sources</p>
        <p style={paragraphStyle}>This includes:</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>information obtained from third parties in connection with pre-employment screening, such as (where relevant to your role) verifying whether you have any criminal convictions, checking your personal credit history, and/or confirming whether you are on any list of sanctioned individuals;</li>
          <li style={listItemStyle}>information obtained through pre-employment health screenings or questionnaires; and</li>
          <li style={listItemStyle}>information about your employment and compensation history, educational background, references and similar information received from recruiters and previous employers.</li>
        </ul>

        <h2 style={headingStyle}>Uses of your personal data</h2>
        <p style={paragraphStyle}>Your personal data may be stored and processed by us in the following ways and for the following purposes:</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>to consider your application (including, in some cases, verifying your qualifications and references with those third parties you name);</li>
          <li style={listItemStyle}>to support you (and your family members') move to the relevant location, where applicable;</li>
          <li style={listItemStyle}>to meet our legal and regulatory obligations;</li>
          <li style={listItemStyle}>the management and administration of the Mubadala business, including the maintenance of centralised databases storing personal data;</li>
          <li style={listItemStyle}>enabling the performance of the functions that each of the Mubadala group businesses may perform relating to regional or global HR decisions within Mubadala;</li>
          <li style={listItemStyle}>compliance with internal policies and procedures within Mubadala;</li>
          <li style={listItemStyle}>to maintain consistent practices and procedures with respect to the collection, use, disclosure, transfer and processing of personal data across Mubadala companies. These practices and procedures include the effective recording, management and administration of personal data;</li>
          <li style={listItemStyle}>to maintain consistent practices and procedures with respect to the recruitment of personnel across Mubadala, including the performance of human resources and other functions of Mubadala;</li>
          <li style={listItemStyle}>for the purposes of equal opportunities monitoring;</li>
          <li style={listItemStyle}>to maintain contact with you in the future and notify you of relevant job vacancies with Mubadala that you might be interested in (please note that if you do not want us to retain your information, or want us to update it at any stage, please contact us in accordance with the "Contacting us" section);</li>
          <li style={listItemStyle}>to exercise or defend our legal rights; or</li>
          <li style={listItemStyle}>to facilitate and manage the recruitment process using AI-powered systems and tools, which may include conducting interviews with AI agents, analysing your responses (including audio and video) and generating interview reports or similar outputs for assessment and evaluation purposes, in accordance with applicable data protection law and Mubadala's internal policies.</li>
        </ul>

        <p style={paragraphStyle}>
          If you do not provide us with certain information (for example, information that is indicated as mandatory or which are necessary to enter into a contract with us), we may not be able to process your application.
        </p>

        <p style={paragraphStyle}>
          Please note that we may use AI when processing personal data for these purposes if permitted by applicable law. However, it is important to note that any decision that is material to you or your application will ultimately be taken by appropriate member of our staff. As such, there will be no "automated decision making" for the purposes of applicable data protection law.
        </p>

        <p style={paragraphStyle}>
          In accordance with applicable data protection law, our use of AI systems is designed to ensure ethical, fair, transparent, secure, and accountable processing of personal data. We are committed to maintaining high standards of data protection and ensuring that any AI systems used are aligned with these principles.
        </p>

        <p style={paragraphStyle}>
          If your application is successful and you are subsequently offered and accept work at Mubadala, the information we collect during the application and recruitment process will become part of your employment record.
        </p>

        <h2 style={headingStyle}>Legal grounds we rely on to process your personal data</h2>
        <p style={paragraphStyle}>We are entitled to use your personal data in these ways because:</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>it is necessary for a contract we have with you, or because we need to in order to take steps in preparation for entering into a contract with you, in particular to consider you for a position at Mubadala;</li>
          <li style={listItemStyle}>the personal data has been made public by you;</li>
          <li style={listItemStyle}>we have legal and regulatory obligations that we have to discharge;</li>
          <li style={listItemStyle}>we may need to in order to establish, exercise or defend our legal rights or for the purpose of legal proceedings; or</li>
          <li style={listItemStyle}>the use of your personal data as described may be necessary for any legitimate business interests we may have as permitted under applicable law, provided that our legitimate interest does not override your rights and freedoms and is in compliance with local law.</li>
        </ul>

        <p style={paragraphStyle}>
          We may rely on your consent where other grounds cannot be relied upon and only where such consent can be validly obtained (including by meeting the requirement that such consent is freely given by you).
        </p>

        <h2 style={headingStyle}>Special categories of personal data and sensitive information</h2>
        <p style={paragraphStyle}>
          The following categories of personal data are considered sensitive under the laws of your jurisdiction and may receive special protection, data related to:
        </p>
        <ul style={listStyle}>
          <li style={listItemStyle}>a natural person's family;</li>
          <li style={listItemStyle}>racial origin, political or philosophical opinions, religious beliefs;</li>
          <li style={listItemStyle}>criminal records; and</li>
          <li style={listItemStyle}>biometric data, or any data related to the health of such person, such as their physical, psychological, mental, genetic, or sexual condition, including information related to health care services provided thereto that reveals his/her health status.</li>
        </ul>

        <p style={paragraphStyle}>
          We will only process special category personal data or sensitive information, or criminal records data where permitted by law and if you have provided explicit consent, the processing is required by employment or social security law, necessary to protect vital interests, relates to data you have made public, is needed for legal proceedings or advice, or as otherwise allowed by law.
        </p>

        <p style={paragraphStyle}>
          As set out below, we have implemented appropriate technical, and organisational security measures designed to secure your personal data against accidental loss and unauthorised access, use, alteration, or disclosure.
        </p>

        <p style={paragraphStyle}>
          We will only retain special categories of personal data for as long as necessary to fulfil the purposes we collected it for.
        </p>

        <h2 style={headingStyle}>Disclosure of your information to third parties</h2>

        <p style={subHeadingStyle}>Within the wider group</p>
        <p style={paragraphStyle}>
          We may disclose your personal data to other members of our group for the purposes described above. We will take steps to ensure that the personal data is accessed only by Mubadala employees that have a need to do so for the purposes described in this notice.
        </p>

        <p style={subHeadingStyle}>Outside of the group</p>
        <p style={paragraphStyle}>
          We may also share your personal data with the following categories of third parties outside of Mubadala:
        </p>
        <ul style={listStyle}>
          <li style={listItemStyle}>with third party agents and contractors for the purposes of providing services to us, which may include the use of AI-powered systems and tools in the recruitment process (such as outsourced HR providers and consultants, background check services, virtual testing and assessment providers, IT and communications providers), as well as professional advisors such as law firms, accountants and auditors;</li>
          <li style={listItemStyle}>if we sell any of our business or assets or are acquired by a third party, in which case we may disclose your personal data to the prospective buyer for due diligence purposes; and</li>
          <li style={listItemStyle}>to the extent required by law, regulation or any court order, for example if we are under a duty to disclose your personal data in order to comply with any legal obligation or request from a regulatory authority, and to establish, exercise or defend our legal and contractual rights.</li>
        </ul>

        <h2 style={headingStyle}>International transfers of personal data</h2>
        <p style={paragraphStyle}>
          To the extent permitted by applicable data protection law, the personal data that we collect from you may be transferred to, and stored at, a destination outside of the jurisdiction in which it was originally collected ("Relevant Location"). It may also be stored and processed by other companies and/or third parties in other countries, which may include destinations outside of the Relevant Location.
        </p>

        <p style={paragraphStyle}>
          Where your personal data is transferred outside of the Relevant Location, we will ensure that it is protected in a manner that is consistent with how your personal data will be protected by us in the Relevant Location. This can be done in a number of ways, for instance:
        </p>
        <ul style={listStyle}>
          <li style={listItemStyle}>the country that we send the data to might be approved by the data protection authority in the Relevant Location as offering an adequate level of protection for your personal data; or</li>
          <li style={listItemStyle}>the recipient might have signed up to a contract based on "model contractual clauses" approved by the data protection authority in the Relevant Location, obliging them to protect your personal data.</li>
        </ul>

        <p style={paragraphStyle}>
          In other circumstances the law may permit us to otherwise transfer your personal data outside the Relevant Location. In all cases, however, we will ensure that any transfer of your personal data is compliant with data protection law.
        </p>

        <p style={paragraphStyle}>
          You can obtain more details about the protection given to your personal data when it is transferred outside the Relevant Location by contacting us in accordance with the "Contacting us" section below.
        </p>

        <h2 style={headingStyle}>Retention of personal data</h2>
        <p style={paragraphStyle}>
          How long we hold your personal data for will vary. The retention period will be determined by various criteria including:
        </p>
        <ul style={listStyle}>
          <li style={listItemStyle}>the purpose for which we are using it - we will need to keep the data for as long as is necessary for that purpose; and</li>
          <li style={listItemStyle}>legal obligations - laws or regulation may set a minimum period for which we have to keep your personal data.</li>
        </ul>

        <h2 style={headingStyle}>Data security</h2>
        <p style={paragraphStyle}>
          We have implemented appropriate technical and organisational security measures designed to secure your personal data against accidental loss and unauthorized access, use, alteration, or disclosure. In addition, we limit access to personal data to those employees, agents, contractors, and other third parties that have a legitimate business need for such access.
        </p>

        <h2 style={headingStyle}>Your rights</h2>
        <p style={paragraphStyle}>
          You have several legal rights in relation to the personal data that we hold about you. These rights include:
        </p>
        <ul style={listStyle}>
          <li style={listItemStyle}>the right to obtain information regarding the processing of your personal data and access to the personal data which we hold about you;</li>
          <li style={listItemStyle}>the right to withdraw your consent to our processing of your personal data at any time. Please note, however, that we may still be entitled to process your personal data if we have another legitimate reason (other than consent) for doing so;</li>
          <li style={listItemStyle}>in some circumstances, the right to receive some personal data in a structured, commonly used and machine-readable format and/or request that we transmit those data to a third party where this is technically feasible. Please note that this right only applies to personal data which you have provided to us;</li>
          <li style={listItemStyle}>the right to request that we rectify your personal data if it is inaccurate or incomplete;</li>
          <li style={listItemStyle}>the right to request that we erase your personal data in certain circumstances. Please note that there may be circumstances where you ask us to erase your personal data, but we are legally entitled to retain it;</li>
          <li style={listItemStyle}>the right to object to, and the right to request that we restrict, our processing of your personal data in certain circumstances. Again, there may be circumstances where you object to, or ask us to restrict, our processing of your personal data but we are legally entitled to continue processing your personal data and / or to refuse that request; and</li>
          <li style={listItemStyle}>the right to lodge a complaint with the data protection regulator (details of which are provided below) if you think that any of your rights have been infringed by us.</li>
        </ul>

        <p style={paragraphStyle}>
          You can exercise your rights by contacting us using the details set out in the "Contacting us" section below.
        </p>

        <h2 style={headingStyle}>Making a privacy complaint</h2>
        <p style={paragraphStyle}>
          You can contact us using the contact details at the bottom of this notice if you have any concerns about how we have handled your personal data. If you are not satisfied with the manner in which we have dealt with your complaint, you may contact the relevant data protection authority in your jurisdiction.
        </p>

        <h2 style={headingStyle}>Contacting us</h2>
        <p style={paragraphStyle}>
          If you would like further information on the collection, use, disclosure, transfer or processing (including location) of your personal data or the exercise of any of the rights listed above, please email questions, comments and requests to{' '}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            style={{
              color: '#171C27',
              textDecoration: 'underline',
            }}
          >
            {CONTACT_DISPLAY}
          </a>
          .
        </p>
      </main>

      {/* Footer */}
      <footer
        className="py-6 px-6 md:px-[120px]"
        style={{
          height: '80px',
          borderTop: '1px solid #F3F4F6',
        }}
      >
        <span
          style={{
            color: '#9CA3AF',
            fontFamily: 'Interstate',
            fontSize: '12.014px',
            fontWeight: 400,
            lineHeight: '16px',
          }}
        >
          {new Date().getFullYear()} Mubadala Investment Company. Internal use only.
        </span>
      </footer>
    </div>
  );
};

const headingStyle: React.CSSProperties = {
  color: '#171C27',
  fontFamily: 'Interstate',
  fontSize: '20px',
  fontWeight: 700,
  letterSpacing: '0.6px',
  marginTop: '32px',
  marginBottom: '16px',
};

const subHeadingStyle: React.CSSProperties = {
  color: '#63615C',
  fontFamily: 'Interstate',
  fontSize: '14px',
  fontWeight: 700,
  letterSpacing: '0.6px',
  marginTop: '20px',
  marginBottom: '8px',
};

const paragraphStyle: React.CSSProperties = {
  color: '#63615C',
  fontFamily: 'Interstate',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.6',
  letterSpacing: '0.4px',
  marginBottom: '12px',
};

const listStyle: React.CSSProperties = {
  marginLeft: '24px',
  marginBottom: '12px',
  listStyleType: 'disc',
  paddingLeft: '16px',
};

const listItemStyle: React.CSSProperties = {
  color: '#63615C',
  fontFamily: 'Interstate',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.6',
  letterSpacing: '0.4px',
  marginBottom: '8px',
};

export default Privacy;
